import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Plus, Minus, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";
import {
  PIZZA_SIZES,
  PIZZAS_COMBINADAS,
  PIZZAS_ESPECIALES,
} from "@/data/menuData";

/**
 * Modos soportados:
 *  - "combinada"        Pizza redonda combinada (chica/med/grande/XL) — hasta 2 sabores de PIZZAS_COMBINADAS
 *  - "especial"         Pizza redonda especial  (chica/med/grande/XL) — hasta 2 sabores de PIZZAS_ESPECIALES
 *  - "cuadrada-combi"   Cuadrada Combinada (precio fijo) — 4 sabores SOLO de PIZZAS_COMBINADAS
 *  - "maxi-combi"       Maxi  Combinada    (precio fijo) — 6 sabores SOLO de PIZZAS_COMBINADAS
 *  - "cuadrada-esp"     Cuadrada Especial  (precio fijo) — 4 sabores de Combinadas O Especiales
 *  - "maxi-esp"         Maxi Especial      (precio fijo) — 6 sabores de Combinadas O Especiales
 */

const ROUND_MODES = ["combinada", "especial"];
const SQUARE_MODES = ["cuadrada-combi", "maxi-combi", "cuadrada-esp", "maxi-esp"];

const slotsByMode = {
  "cuadrada-combi": 4,
  "maxi-combi": 6,
  "cuadrada-esp": 4,
  "maxi-esp": 6,
};

const flavorsByMode = (mode) => {
  if (mode === "combinada") return PIZZAS_COMBINADAS;
  if (mode === "especial") return [...PIZZAS_COMBINADAS, ...PIZZAS_ESPECIALES];
  if (mode === "cuadrada-combi" || mode === "maxi-combi") return PIZZAS_COMBINADAS;
  if (mode === "cuadrada-esp" || mode === "maxi-esp")
    return [...PIZZAS_COMBINADAS, ...PIZZAS_ESPECIALES];
  return [];
};

const orillaCostBySize = {
  Chica: 55,
  Mediana: 60,
  Grande: 65,
  "Extra Grande": 70,
};

export const PizzaCustomizer = ({ mode, initialFlavor, fixedItem, onClose }) => {
  const { addItem } = useCart();
  const isRound = ROUND_MODES.includes(mode);
  const isSquare = SQUARE_MODES.includes(mode);

  // Sizes only for round pizzas
  const sizeOptions = useMemo(() => {
    if (mode === "combinada") return PIZZA_SIZES.combinadas;
    if (mode === "especial") return PIZZA_SIZES.especiales;
    return [];
  }, [mode]);

  const [size, setSize] = useState(sizeOptions[0]?.size || null);
  const [flavors, setFlavors] = useState(initialFlavor ? [initialFlavor] : []);
  const [orillaQueso, setOrillaQueso] = useState(false);
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState(1);

  const flavorList = flavorsByMode(mode);
  const maxSlots = isSquare ? slotsByMode[mode] : 2;

  const toggleFlavor = (name) => {
    setFlavors((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      if (prev.length >= maxSlots) {
        // replace first slot to keep last selected
        return [...prev.slice(1), name];
      }
      return [...prev, name];
    });
  };

  // For square modes: allow incrementing/decrementing the count of each flavor (repeats allowed)
  const incFlavor = (name) => {
    setFlavors((prev) => (prev.length >= maxSlots ? prev : [...prev, name]));
  };
  const decFlavor = (name) => {
    setFlavors((prev) => {
      const idx = prev.lastIndexOf(name);
      if (idx < 0) return prev;
      const copy = [...prev];
      copy.splice(idx, 1);
      return copy;
    });
  };
  const countFlavor = (name) => flavors.filter((n) => n === name).length;

  // Pricing
  const basePrice = useMemo(() => {
    if (isRound) {
      const found = sizeOptions.find((s) => s.size === size);
      return found?.price || 0;
    }
    // Square: fixed price on the fixedItem
    return fixedItem?.price || 0;
  }, [isRound, sizeOptions, size, fixedItem]);

  const orillaCost = useMemo(() => {
    if (!orillaQueso) return 0;
    if (isRound) return orillaCostBySize[size] || 0;
    if (mode === "cuadrada-combi" || mode === "cuadrada-esp") return PIZZA_SIZES.orilla_queso.cuadrada;
    if (mode === "maxi-combi" || mode === "maxi-esp") return PIZZA_SIZES.orilla_queso.maxi;
    return 0;
  }, [orillaQueso, isRound, size, mode]);

  const unitPrice = basePrice + orillaCost;

  const canSubmit =
    flavors.length > 0 && (isSquare ? flavors.length <= maxSlots : true);

  const handleAdd = () => {
    if (!canSubmit) return;
    const titleBase = isRound
      ? `Pizza ${size} ${mode === "combinada" ? "Combinada" : "Especial"}`
      : fixedItem?.name;
    // Summarize repeats: ["Pastor","Pastor","Hawaiana"] -> "Pastor x2 / Hawaiana"
    const counts = flavors.reduce((acc, n) => {
      acc[n] = (acc[n] || 0) + 1;
      return acc;
    }, {});
    const summary = Object.entries(counts)
      .map(([n, c]) => (c > 1 ? `${n} x${c}` : n))
      .join(" / ");
    const name = `${titleBase} — ${summary}`;
    addItem({
      type: "pizza",
      name,
      category: isRound
        ? mode === "combinada"
          ? "Pizzas Combinadas"
          : "Pizzas Especiales"
        : "Cuadrada/Maxi",
      size: isRound ? size : null,
      flavors,
      orillaQueso,
      orillaCost,
      basePrice,
      unitPrice,
      notes: notes.trim(),
      quantity: qty,
    });
    onClose();
  };

  const title = isRound
    ? `Armar Pizza ${mode === "combinada" ? "Combinada" : "Especial"}`
    : `Armar ${fixedItem?.name}`;
  const subtitle = isRound
    ? "Elige el tamaño y hasta 2 sabores (mitad y mitad)"
    : `Elige de 1 a ${maxSlots} combinaciones${mode.endsWith("-combi") ? " (solo combinadas)" : " (combinadas o especiales)"} — puedes repetir el mismo`;

  return (
    <div
      data-testid="pizza-customizer"
      className="fixed inset-0 z-[400] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FAF7F0] w-full sm:max-w-2xl sm:rounded-2xl rounded-t-3xl max-h-[92vh] flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-[#1A1A1A]/10 bg-[#2B3618] text-white sm:rounded-t-2xl rounded-t-3xl">
          <div className="flex-1 min-w-0 pr-3">
            <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold">{title}</h3>
            <p className="font-['DM_Sans'] text-xs sm:text-sm text-white/80 mt-1">{subtitle}</p>
          </div>
          <button
            data-testid="customizer-close-btn"
            onClick={onClose}
            className="w-9 h-9 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-5 flex-1">
          {/* Size for round */}
          {isRound && (
            <div>
              <h4 className="font-['DM_Sans'] text-xs uppercase tracking-[0.18em] font-bold text-[#556B2F] mb-2">
                Tamaño
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {sizeOptions.map((s) => (
                  <button
                    key={s.size}
                    data-testid={`size-${s.size.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setSize(s.size)}
                    className={`rounded-xl border-2 p-3 text-center transition-all ${
                      size === s.size
                        ? "border-[#556B2F] bg-[#556B2F] text-white shadow-md"
                        : "border-[#1A1A1A]/15 bg-white hover:border-[#556B2F]/60"
                    }`}
                  >
                    <span className="block font-['DM_Sans'] text-xs font-semibold">{s.size}</span>
                    {s.slices && <span className="block text-[10px] opacity-80">{s.slices}</span>}
                    <span className="block font-bold mt-1">${s.price}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Flavors */}
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <h4 className="font-['DM_Sans'] text-xs uppercase tracking-[0.18em] font-bold text-[#556B2F]">
                Sabores {isRound ? "(hasta 2)" : `(${flavors.length}/${maxSlots})`}
              </h4>
              {flavors.length > 0 && (
                <button
                  onClick={() => setFlavors([])}
                  className="text-[11px] font-['DM_Sans'] text-[#C41E3A] hover:underline"
                >
                  Limpiar
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[260px] overflow-y-auto pr-1">
              {flavorList.map((f) => {
                if (isSquare) {
                  const count = countFlavor(f.name);
                  const disabled = flavors.length >= maxSlots;
                  return (
                    <div
                      key={f.name}
                      data-testid={`flavor-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
                      className={`rounded-lg border-2 p-2.5 transition-all relative ${
                        count > 0
                          ? "border-[#C41E3A] bg-[#C41E3A]/10"
                          : "border-[#1A1A1A]/12 bg-white"
                      }`}
                    >
                      <span className="block font-['DM_Sans'] text-xs font-bold text-[#1A1A1A] pr-2">
                        {f.name}
                      </span>
                      {f.description && (
                        <span className="block font-['DM_Sans'] text-[10px] text-[#4A4A4A] mt-0.5 line-clamp-2">
                          {f.description}
                        </span>
                      )}
                      <div className="mt-2 flex items-center justify-between">
                        <div className="inline-flex items-center bg-white border border-[#1A1A1A]/15 rounded-full">
                          <button
                            data-testid={`flavor-dec-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
                            onClick={() => decFlavor(f.name)}
                            disabled={count === 0}
                            className="w-6 h-6 flex items-center justify-center hover:bg-[#F3F4F6] rounded-l-full disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="w-6 text-center text-xs font-bold">{count}</span>
                          <button
                            data-testid={`flavor-inc-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
                            onClick={() => incFlavor(f.name)}
                            disabled={disabled}
                            className="w-6 h-6 flex items-center justify-center hover:bg-[#F3F4F6] rounded-r-full disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
                const selected = flavors.includes(f.name);
                return (
                  <button
                    key={f.name}
                    data-testid={`flavor-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => toggleFlavor(f.name)}
                    className={`text-left rounded-lg border-2 p-2.5 transition-all relative ${
                      selected
                        ? "border-[#C41E3A] bg-[#C41E3A]/10"
                        : "border-[#1A1A1A]/12 bg-white hover:border-[#C41E3A]/40"
                    }`}
                  >
                    {selected && (
                      <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#C41E3A] text-white flex items-center justify-center">
                        <Check className="w-3 h-3" />
                      </span>
                    )}
                    <span className="block font-['DM_Sans'] text-xs font-bold text-[#1A1A1A] pr-5">
                      {f.name}
                    </span>
                    {f.description && (
                      <span className="block font-['DM_Sans'] text-[10px] text-[#4A4A4A] mt-0.5 line-clamp-2">
                        {f.description}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {isSquare && flavors.length === 0 && (
              <p className="mt-2 text-xs text-[#C41E3A] font-['DM_Sans']">
                Elige al menos 1 sabor (puedes repetir el mismo hasta {maxSlots} veces).
              </p>
            )}
          </div>

          {/* Orilla de queso */}
          <div>
            <label className="flex items-center justify-between bg-white border border-[#1A1A1A]/15 rounded-xl p-3 cursor-pointer">
              <div>
                <span className="font-['DM_Sans'] text-sm font-semibold text-[#1A1A1A]">
                  Agregar orilla de queso
                </span>
                <span className="block text-xs text-[#4A4A4A]">
                  Costo extra: +$
                  {isRound
                    ? orillaCostBySize[size] || 0
                    : mode === "cuadrada-combi" || mode === "cuadrada-esp"
                    ? PIZZA_SIZES.orilla_queso.cuadrada
                    : PIZZA_SIZES.orilla_queso.maxi}
                </span>
              </div>
              <input
                type="checkbox"
                checked={orillaQueso}
                onChange={(e) => setOrillaQueso(e.target.checked)}
                data-testid="orilla-queso-checkbox"
                className="w-5 h-5 accent-[#C41E3A]"
              />
            </label>
          </div>

          {/* Notes */}
          <div>
            <h4 className="font-['DM_Sans'] text-xs uppercase tracking-[0.18em] font-bold text-[#556B2F] mb-2">
              Notas (opcional)
            </h4>
            <textarea
              data-testid="customizer-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej. Sin cebolla, extra queso..."
              rows={2}
              className="w-full bg-white border border-[#1A1A1A]/15 rounded-xl p-3 text-sm font-['DM_Sans'] focus:outline-none focus:border-[#556B2F]"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#1A1A1A]/10 p-4 bg-white sm:rounded-b-2xl flex items-center gap-3">
          <div className="flex items-center bg-[#F3F4F6] rounded-full">
            <button
              data-testid="qty-decrease"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#E5E7EB]"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-7 text-center font-bold font-['DM_Sans']">{qty}</span>
            <button
              data-testid="qty-increase"
              onClick={() => setQty((q) => q + 1)}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#E5E7EB]"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <button
            data-testid="add-to-cart-confirm"
            disabled={!canSubmit}
            onClick={handleAdd}
            className="flex-1 bg-[#C41E3A] hover:bg-[#9A172D] disabled:bg-[#9A172D]/40 disabled:cursor-not-allowed text-white font-['DM_Sans'] font-bold py-3 rounded-full transition-colors flex items-center justify-center gap-2"
          >
            Agregar ${unitPrice * qty}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

/* Simple item modal (non-pizza) */
export const ItemCustomizer = ({ item, category, onClose }) => {
  const { addItem } = useCart();
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState(1);

  const handleAdd = () => {
    addItem({
      type: "simple",
      name: item.name,
      category,
      basePrice: item.price,
      unitPrice: item.price,
      notes: notes.trim(),
      quantity: qty,
    });
    onClose();
  };

  return (
    <div
      data-testid="item-customizer"
      className="fixed inset-0 z-[400] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FAF7F0] w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl shadow-2xl"
      >
        <div className="flex items-start justify-between p-5 border-b border-[#1A1A1A]/10 bg-[#2B3618] text-white sm:rounded-t-2xl rounded-t-3xl">
          <div className="flex-1 min-w-0 pr-3">
            <h3 className="font-['Playfair_Display'] text-lg md:text-xl font-bold">{item.name}</h3>
            {item.description && (
              <p className="font-['DM_Sans'] text-xs text-white/80 mt-1">{item.description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <h4 className="font-['DM_Sans'] text-xs uppercase tracking-[0.18em] font-bold text-[#556B2F] mb-2">
              Notas (opcional)
            </h4>
            <textarea
              data-testid="item-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej. Sin cebolla, extra salsa..."
              rows={2}
              className="w-full bg-white border border-[#1A1A1A]/15 rounded-xl p-3 text-sm font-['DM_Sans'] focus:outline-none focus:border-[#556B2F]"
            />
          </div>
        </div>

        <div className="border-t border-[#1A1A1A]/10 p-4 bg-white sm:rounded-b-2xl flex items-center gap-3">
          <div className="flex items-center bg-[#F3F4F6] rounded-full">
            <button
              data-testid="item-qty-decrease"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#E5E7EB]"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-7 text-center font-bold font-['DM_Sans']">{qty}</span>
            <button
              data-testid="item-qty-increase"
              onClick={() => setQty((q) => q + 1)}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#E5E7EB]"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <button
            data-testid="item-add-confirm"
            onClick={handleAdd}
            className="flex-1 bg-[#C41E3A] hover:bg-[#9A172D] text-white font-['DM_Sans'] font-bold py-3 rounded-full transition-colors"
          >
            Agregar ${item.price * qty}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export const CustomizerModalRoot = ({ state, onClose }) => (
  <AnimatePresence>
    {state?.type === "pizza" && (
      <PizzaCustomizer
        mode={state.mode}
        initialFlavor={state.initialFlavor}
        fixedItem={state.fixedItem}
        onClose={onClose}
      />
    )}
    {state?.type === "item" && (
      <ItemCustomizer item={state.item} category={state.category} onClose={onClose} />
    )}
  </AnimatePresence>
);
